import React, { FC, useEffect, useState } from 'react';
import Head from 'next/head';
import PageHeader from '~/components/PageHeader';
import { Star, StopwatchRed } from '~/components/Icons/outline';
import AddressCheckStyled from '~/styles/pages/AddressCheckStyled';
import OneColumn from '~/components/Layout/OneColumn';
import { useDispatch, useSelector } from 'react-redux';
import { setCallInNumber } from '~/redux/actions/common';
import { setAmexOffer } from '~/redux/actions/global';
import { DEFAULT_SUBTITLE } from '~/constants/address';
import AddressForm from '~/components/AddressForm';
import Modal from '~/components/Modal';
import ResumeModal from '~/components/Modal/screens/Resume';
import ReturnOffer from '~/components/Modal/screens/ReturnOffer';
import { ModalKeys } from '~/constants/modal';
import Cookies from 'js-cookie';
import { setCookie } from '~/helpers/cookie';
import clientSide from '~/helpers/clientSide';
import { MonarchSlot, useMonarchSlot } from '@redventures/cohesion-utils-react';
import { cartConfigAddressCheckDefaultValue } from '../../monarch/Config | Address Check/default';
import { clearServiceabilityResults } from '~/redux/actions/address';
import { ReduxState } from '~/types/redux';
import AddressCheckOfferPlacement from '~/components/AddressForm/AddressCheckOfferPlacement';
import { useBuy } from '~/hooks/pages/useBuy';
import { selectActiveModal } from '~/redux/selectors/modal';
import { useModal } from '~/hooks/useModal';
import { getReturnVisitorCookie, setReturnVisitorCookie } from '~/helpers/returnVisitorCookie';
import { useReturnVisitorOfferEligibility } from '~/hooks/useReturnVisitorOfferEligibility';
import Chatbot from '~/components/ChatBot';

const iconMap = {
  star: Star,
  stopwatch: StopwatchRed,
};

const AddressCheck: FC = (): JSX.Element => {
  const { apiVersion, selectedAddress, buttonState, setSelectedAddress, handleAddressFormSubmit, handleInputChange } =
    useBuy();

  // Use Redux modal state and modal controls
  const activeModal = useSelector(selectActiveModal);
  const { closeModal } = useModal();

  const dispatch = useDispatch();
  const {
    address,
    cookiePass,
    tracking: { sessionId: trackingSessionId },
  } = useSelector((store: ReduxState) => store);
  const [showForm, setShowForm] = useState(false);
  const shownReturnVisitorOffer = useReturnVisitorOfferEligibility();

  useEffect(() => {
    if (!Cookies.get('frontierSiteDetailPredictive')) {
      dispatch(clearServiceabilityResults());
    }

    const isAmexOffer = window?.location?.href?.toLowerCase().includes('amex');
    if (isAmexOffer) {
      setCookie('isAmexOffer', true, { otGroup: 'C0004' });
      dispatch(setCallInNumber('877-837-6222'));
      dispatch(setAmexOffer(true));
    } else {
      setCookie('isAmexOffer', false, { otGroup: 'C0004' });
    }
  }, []);

  // Wait to show address check form until we've ensured a user is not in the cookie pass/Allconnect flow
  useEffect(() => {
    if (!cookiePass) {
      setShowForm(true);
    }

    // Fallback after 2 seconds so that a user never sits on /buy unable to submit an address
    setTimeout(() => {
      setShowForm(true);
    }, 2000);
  }, [cookiePass]);

  // RVO cookie creation and update if Cohesion session ID changes.
  useEffect(() => {
    if (!trackingSessionId) return;

    setReturnVisitorCookie({ shownReturnVisitorOffer });
  }, [trackingSessionId, shownReturnVisitorOffer]);

  // Grabbing Address Check Page Config slot's metadata
  const metadata = useMonarchSlot('addressCheck', {
    default: cartConfigAddressCheckDefaultValue,
  })?.data;

  const getTitle = (copy: string) => {
    // Show specific copy when product is selected on landing page
    if (clientSide && window?.sessionStorage?.getItem('selectedProductId')) {
      return 'Enter your address to continue checkout and view exclusive offers';
    }
    return copy ?? 'Enter your address to view plans and exclusive offers.';
  };

  const getSubtitle = (enabled: boolean, copy: string) => {
    // Show specific copy when product is selected on landing page
    if (clientSide && window?.sessionStorage?.getItem('selectedProductId')) {
      return 'You may be eligible for special offers, like free installation and a Wi-Fi router included in your plan.';
    }
    return enabled !== false ? copy || DEFAULT_SUBTITLE : '';
  };

  const Icon = iconMap[metadata?.subtitle?.icon] || Star;

  return (
    <OneColumn>
      <Head>
        <title>Frontier Availability Check | Internet Service and Coverage</title>
        <meta
          name="description"
          content="Discover if high-speed fiber internet is near you with our Frontier availability check—enter your address and check in seconds."
        />
      </Head>
      <MonarchSlot slotId="cartBannerModern" />
      <AddressCheckStyled showForm={showForm}>
        <MonarchSlot slotId="addressCheck">
          <>
            <PageHeader
              title={getTitle(metadata?.title?.copy)}
              subtitle={{
                icon: <Icon />,
                text: getSubtitle(metadata?.subtitle?.enabled === 'true', metadata?.subtitle?.copy),
              }}
              headingLevel={metadata?.headingLevel === '2' ? 2 : 1}
            />
            <AddressForm
              apiVersion={apiVersion}
              onSubmit={handleAddressFormSubmit}
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              handleInputChange={handleInputChange}
              buttonState={buttonState}
              placeholder="Enter your address"
              title={metadata?.title?.copy || ''}
              {...(metadata && { ...metadata, title: undefined })}
            />
            {metadata?.offerPlacement?.enabled === true && (
              <AddressCheckOfferPlacement
                title={metadata?.offerPlacement?.title || ''}
                tiles={metadata?.offerPlacement?.tiles || []}
              />
            )}
          </>

          <Modal
            zIndex={9998}
            visible={[ModalKeys.RESUME, ModalKeys.RETURN_OFFER].includes(activeModal)}
            handleOnClose={closeModal}
          >
            <ResumeModal visible={activeModal === ModalKeys.RESUME} apiVersion={apiVersion} />
            <ReturnOffer
              visible={activeModal === ModalKeys.RETURN_OFFER}
              hasAddress={Boolean(address?.install?.address) || Boolean(getReturnVisitorCookie()?.controlNumber)}
              isResumeQuoteUser={Boolean(Cookies.get('quoteId'))}
              apiVersion={apiVersion}
              onClose={closeModal}
            />
          </Modal>
        </MonarchSlot>
      </AddressCheckStyled>
      <Chatbot />
    </OneColumn>
  );
};

AddressCheck.displayName = 'AddressCheck';

export default AddressCheck;
